import React from "react";
import BottomSheet from "../../../components/common/bottom-sheet";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import { formatTimeWithAM } from "../../../utils/date/format-date";
import {
  getDayOfWeekIndex,
  getTomorrowDayOfWeekIndex,
} from "../../../utils/reserve-time";

interface ReserveConfirmBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (isTomorrow: boolean) => void;
  exposeType: "오늘" | "내일" | "X";
  store: any;
}

const getKoreanDayOfWeek = (dayOfWeek: number | string) => {
  const days = [
    "월요일",
    "화요일",
    "수요일",
    "목요일",
    "금요일",
    "토요일",
    "일요일",
  ];
  return days[Number(dayOfWeek)];
};

const ReserveConfirmBottomSheet: React.FC<ReserveConfirmBottomSheetProps> = ({
  isOpen,
  onClose,
  onConfirm,
  exposeType,
  store,
}) => {
  const dayOfWeek = getDayOfWeekIndex(new Date());
  const tomorrowOfWeek = getTomorrowDayOfWeekIndex();
  const now = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const getReserveTime = () => {
    return (
      formatTimeWithAM(store?.reserveTime?.[dayOfWeek]?.start) +
      " ~ " +
      formatTimeWithAM(store?.reserveTime?.[dayOfWeek]?.end)
    );
  };

  const getReserveTimeBefore30Minutes = (isTomorrow: boolean) => {
    const start = isTomorrow
      ? store?.reserveTime?.[tomorrowOfWeek]?.start
      : store?.reserveTime?.[dayOfWeek]?.start;
    if (!start) return "-";
    const [startHour, startMinute] = start.split(":").map(Number);
    const startDate = new Date();
    startDate.setHours(startHour, startMinute - 30);
    const startBefore30Minutes = `${startDate
      .getHours()
      .toString()
      .padStart(2, "0")}:${startDate.getMinutes().toString().padStart(2, "0")}`;
    return formatTimeWithAM(startBefore30Minutes);
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center">
        <FadeInWrapper
          className={`mb-4 text-center ${
            exposeType === "내일" ? "text-2xl font-bold" : "text-lg font-bold"
          }`}
        >
          {exposeType === "내일" ? "내일 픽업건이에요" : "👋 한 번 더 체크"}
        </FadeInWrapper>
        <FadeInWrapper className="text-center mb-6 text-md text-gray-600 flex flex-col items-start w-full">
          {exposeType === "오늘" && (
            <>
              <span className="text-start font-bold text-2xl">
                {getReserveTime()}
                <span className="text-gray-500 font-normal text-lg ml-2 ">
                  픽업
                </span>
              </span>
              <br />
              <span className="font-bold text-2xl">
                {getReserveTimeBefore30Minutes(false)}
                <span className="text-gray-500 font-normal text-lg ml-2">
                  확정
                </span>
              </span>
            </>
          )}
          {exposeType === "내일" && (
            <>
              <div className="w-full flex flex-col items-center mb-4">
                <div className="flex gap-3 w-full justify-center items-center">
                  {/* 오늘 */}
                  <div className="flex flex-col items-center justify-center p-2 rounded-lg border-2 border-gray-300 bg-gray-100 w-[70px] h-[70px]">
                    <div className="text-[10px] text-gray-600 font-medium mb-0.5">
                      오늘
                    </div>
                    <div className="text-base font-bold text-gray-600 my-0.5">
                      {now.getDate()}
                    </div>
                    <div className="text-[10px] text-gray-600 font-medium">
                      {getKoreanDayOfWeek(dayOfWeek).replace("요일", "")}
                    </div>
                  </div>

                  {/* 화살표 */}
                  <div className="flex items-center text-gray-400 text-xl">
                    →
                  </div>

                  {/* 내일 - 강조 */}
                  <div className="flex flex-col items-center p-4 rounded-xl border-[3px] border-purple-500 bg-purple-50 flex-1 max-w-[140px] shadow-lg relative">
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      픽업
                    </div>
                    <div className="text-xs text-purple-600 font-bold mb-1 mt-2">
                      내일
                    </div>
                    <div className="text-5xl font-bold text-black mb-1">
                      {tomorrow.getDate()}
                    </div>
                    <div className="text-sm text-purple-600 font-bold">
                      {getKoreanDayOfWeek(tomorrowOfWeek)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </FadeInWrapper>

        <FadeInWrapper className="flex justify-center gap-2 w-full">
          <button
            onClick={onClose}
            className="w-1/3 px-4 py-3 bg-gray-200 rounded-3xl font-bold"
          >
            취소
          </button>
          <button
            onClick={() => onConfirm(exposeType === "내일")}
            className="w-2/3 px-4 py-3 bg-[#1562fc] text-white rounded-3xl font-bold"
          >
            확인했습니다
          </button>
        </FadeInWrapper>
        <FadeInWrapper className="text-center text-gray-500 font-semibold text-sm py-1">
          지정된 픽업 시간에만 픽업 가능
        </FadeInWrapper>
      </div>
    </BottomSheet>
  );
};

export default ReserveConfirmBottomSheet;
